import angular from 'angular';

import { colors } from '../../constants';
import userIcon from '../../assets/svg/UI/employee-white.svg';
import locationIcon from '../../assets/svg/UI/location-white.svg';
import positionIcon from '../../assets/svg/UI/position-white.svg';
import groupIcon from '../../assets/svg/UI/groups-white.svg';

import personaPickerUrl from './personaPicker.html';

const HEADER_HEIGHT = 45;
const CONTENT_HEIGHT_TWO_ROWS = 42;
const CONTENT_HEIGHT_ONE_ROW = 36;
const CREATE_NEW_HEIGHT = 38;

const ICONS_MAP = {
  user: userIcon,
  everyone: userIcon,
  location: locationIcon,
  position: positionIcon,
  group: groupIcon,
};

export default function personaPicker(
  Restangular,
  TagsService,
  Authentication,
  Groups,
  Users,
  PersonaCollection,
  Utils,
  $parse,
  $timeout,
  $document,
  $q
) {
  'ngInject';

  function _setUpScopeVariables($scope, $attrs) {
    if ('placeholder' in $attrs) {
      $scope.placeholder = $attrs.placeholder;
    } else {
      $scope.placeholder = 'Add users, positions, locations or groups';
    }

    if ('onSelected' in $attrs) {
      $scope.onSelected = $parse($attrs.onSelected);
    }

    if ('hideEveryone' in $attrs) {
      $scope.hideEveryone = true;
    }

    if ('hideTags' in $attrs) {
      $scope.hideTags = true;
    }

    if ('focusOnId' in $attrs) {
      $scope.ctrl.focusOnId = $attrs.focusOnId;
    }
  }

  return {
    restrict: 'E',
    scope: {
      personas: '=',
      search: '=?',
      reversed: '=?',
      createEnabled: '=?',
      createType: '=?',
      createCallback: '=?',
      groupNameOnly: '=?',
      groupCountOnTag: '<?',
      groupIconOnTag: '<?',
      selectAppearance: '<?',
      onChange: '&?',
      highlightedPersona: '=?',
      onFocus: '&?',
      bindSingleValue: '<?',
      hideSecondRow: '<?',
      forceShowGroupHeaders: '<?',
      specialPersonas: '<?',
      onSpecialPersonasSelect: '&?',
    },
    replace: true,
    require: ['ngModel', '?^form'],
    templateUrl: personaPickerUrl,
    controllerAs: 'ctrl',
    controller($scope, $element) {
      'ngInject';

      let $input;
      const controller = this;

      function _getDefaultPersona(personaName) {
        const $$new = true;
        switch ($scope.createType) {
          case 'employee': {
            const fullname = personaName;
            let { name, lastname } = Utils.extractName(
              fullname,
              Authentication.user.settings.showLastNameFirst
            );
            if (lastname && lastname.endsWith(',')) {
              lastname = lastname.slice(0, -1);
            }

            return { $$new, name, lastname, fullname, type: 'user' };
          }

          case 'position':
            return {
              $$new,
              name: personaName,
              type: $scope.createType,
              members: [],
              color: colors.getRandom(),
              displayName: `${personaName} (${$scope.createType})`,
            };

          case 'location':
            return {
              $$new,
              name: personaName,
              type: $scope.createType,
              members: [],
              timezone: Authentication.user.timezone,
              displayName: `${personaName} (${$scope.createType})`,
            };

          case 'group':
            return {
              $$new,
              name: personaName,
              type: $scope.createType,
              members: [],
              displayName: `${personaName} (${$scope.createType})`,
            };

          case 'tag':
            return {
              $$new,
              name: personaName,
              type: $scope.createType,
              displayName: `${personaName} (${$scope.createType})`,
              description: '',
            };

          default:
            throw new Error(
              '[persona-picker] createType must be: employee, location, position, tag or group'
            );
        }
      }

      controller.personaSelected = function personaSelected(persona) {
        if (persona.$create) {
          const newPersona = _getDefaultPersona($scope.search);
          return controller.personaSelected(newPersona);
        }

        if (!angular.isArray($scope.selected)) {
          $scope.selected = [];
        }

        $scope.search = '';
        if ($scope.onSelected) {
          $scope.onSelected($scope.$parent, { $item: persona });
        } else {
          $scope.selected.push(persona);
        }

        $element[0].querySelector('input').focus();
      };

      controller.removeSelectedPersona = function removeSelectedPersona(persona) {
        const index = $scope.selected.indexOf(persona);
        $scope.selected.splice(index, 1);
      };

      controller.selectedPersonasClicked = function selectedPersonasClicked(e) {
        if (/selected-persons/.test(e.target.className)) {
          if (!$input || $input.length) {
            $input = $element.find('input');
          }
          $input[0].focus();
        }
      };

      controller.stagedToDelete = false;
      controller.onEmptyBackspace = function onEmptyBackspace() {
        if (
          $scope.selected?.length > 0 &&
          !$scope.nonDeletable.includes($scope.selected[$scope.selected.length - 1].id)
        ) {
          if (controller.stagedToDelete) {
            const lastPersona = $scope.selected[$scope.selected.length - 1];
            if (!$scope.nonDeletable.includes(lastPersona.id)) {
              $scope.$evalAsync(() => {
                controller.removeSelectedPersona(lastPersona);
              });
            }
          } else {
            controller.stagedToDelete = true;
          }
        }
      };

      const personaTypeOrder = {
        everyone: 0,
        location: 1,
        position: 2,
        group: 3,
        user: 4,
        tag: 5,
      };

      controller.orderByType = function orderByType(persona) {
        return personaTypeOrder[persona.type];
      };

      controller.filterEveryone = function filterEveryone(persona) {
        return persona.type !== 'everyone' || !$scope.hideEveryone;
      };

      PersonaCollection.loadData();
      controller.getPersonaDetails = function getPersonaDetails(persona) {
        if (persona.$$new) {
          return persona;
        }

        if (persona.type === 'tag') {
          return PersonaCollection.tags.find((t) => t.id === persona.id) || persona;
        }

        if ($scope.specialPersonas?.find((p) => p.id === persona.id)) {
          return persona;
        }

        return (
          PersonaCollection.personasDict[persona.id] ||
          Restangular.restangularizeElement(null, persona, 'users')
        );
      };

      controller.getSize = function getSize(persona, items) {
        if (persona.$create) {
          return CREATE_NEW_HEIGHT;
        }

        const index = items.indexOf(persona);
        let size = 0;

        if (
          !$scope.hideGroupHeaders &&
          (index === 0 || items[index - 1][$scope.groupBy] !== persona[$scope.groupBy])
        ) {
          size += HEADER_HEIGHT;
        }

        const contentHeight = $scope.hideSecondRow
          ? CONTENT_HEIGHT_ONE_ROW
          : CONTENT_HEIGHT_TWO_ROWS;
        return size + contentHeight;
      };

      controller.getGroupMembersCount = function getGroupMembersCount(persona) {
        return persona.members
          ? persona.members
              .map((m) => PersonaCollection.personasDict[m.id])
              .filter((u) => u && u.active).length
          : 0;
      };

      controller.getPersonaLabel = (persona) => {
        if (persona.type === 'user') {
          return persona.fullname;
        }

        if ($scope.groupNameOnly) {
          return persona.name;
        }

        if ($scope.groupCountOnTag) {
          const count = controller.getGroupMembersCount(persona) || 0;
          return `${persona.name} (${count} Employee${count !== 1 ? 's' : ''})`;
        }

        return persona.displayName;
      };

      controller.getPersonaIcon = (persona) => ICONS_MAP[persona.type];
    },
    link($scope, $element, $attrs, [$ngModel, $form]) {
      $scope.$watchCollection(
        () => $ngModel.$viewValue,
        (val) => {
          if ($scope.bindSingleValue) {
            $scope.selected = val ? [val] : [];
          } else {
            $scope.selected = val;
          }
        }
      );

      $scope.isDisabled = false;

      _setUpScopeVariables($scope, $attrs);

      if ('groupBy' in $attrs) {
        let groupSorter = null;

        if ('groupBySort' in $attrs) {
          groupSorter = $parse($attrs.groupBySort);
        }

        $scope.ctrl.orderByType = function orderByType(persona) {
          if (groupSorter) {
            return groupSorter($scope.$parent, { $group: persona[$attrs.groupBy] });
          }

          return persona[$attrs.groupBy];
        };

        $scope.groupBy = $attrs.groupBy;
        $scope.customGroupBy = true;
      } else {
        $scope.groupBy = 'type';
      }

      let _filterItems = true;
      let _items = [];
      $scope.$watchCollection('selected', (val, prevVal) => {
        _filterItems = true;
        $scope.ctrl.stagedToDelete = false;

        const specialPersonasIds =
          $scope.specialPersonas?.reduce((dict, p) => ((dict[p.id] = p), dict), {}) || {};
        const specialPersonasInVal = val?.filter((p) => p.id in specialPersonasIds);

        if (specialPersonasInVal?.length) {
          $scope.onSpecialPersonasSelect({ $personas: specialPersonasInVal });
          $scope.selected = $scope.selected.filter((s) => !(s.id in specialPersonasIds));
          return;
        }

        if ($scope.bindSingleValue) {
          $ngModel.$setViewValue(val?.length ? val[0] : undefined);
        } else {
          $ngModel.$setViewValue(val);
        }

        if ($scope.onChange) {
          $scope.onChange();
        }

        _handleMaxAttrs(val);
        _handleMinAttrs(val);

        $ngModel.$validate();

        if ($form && (val || []).length !== (prevVal || []).length) {
          $form.$setDirty();
        }
      });

      function _handleMinAttrs(val) {
        if ('min' in $attrs) {
          const minTags = Number($attrs.min);
          if (!val || val.length < minTags) {
            $ngModel.$setValidity('min', false);
          } else {
            $ngModel.$setValidity('min', true);
          }
        }
      }

      function _handleMaxAttrs(val) {
        if ('max' in $attrs) {
          const maxTags = Number($attrs.max);
          if (maxTags > -1 && val && val.length >= maxTags) {
            if (val.length > maxTags) {
              $ngModel.$setValidity('max', false);
            } else {
              $ngModel.$setValidity('max', true);
            }
            $scope.isDisabled = true;
          } else {
            $ngModel.$setValidity('max', true);
            $scope.isDisabled = false;
          }
        }
      }

      $scope.getSearchItems = function getSearchItems() {
        if (!_filterItems) {
          return _items;
        }

        _items = $scope.$eval(
          `personas.concat(specialPersonas || [])
                    | filter:ctrl.filterEveryone
                    | filter:{searchValue: search}
                    | filterOut:selected:'id'
                    | orderBy:'normalizedDisplayName'
                    | orderBy:ctrl.orderByType`
        );

        $scope.hideGroupHeaders =
          !$scope.forceShowGroupHeaders &&
          _items &&
          _items.length &&
          _items.every((item) => item[$scope.groupBy] === _items[0][$scope.groupBy]);

        if ($scope.createEnabled && $scope.search) {
          _items.push({ $create: true });
        }

        _filterItems = false;
        return _items;
      };

      $scope.$watch('search', (val) => {
        _filterItems = true;
        $scope.ctrl.stagedToDelete = false;
        $ngModel.$setValidity('leftover-text', !val);
        $ngModel.$validate();

        $scope.$evalAsync(() => {
          $scope.$broadcast('vsRepeatTrigger');
        });
      });

      $scope.$watchCollection('personas', () => {
        _filterItems = true;
      });

      $scope.nonDeletable =
        'nonDeletable' in $attrs ? $scope.$parent.$eval($attrs.nonDeletable) : [];

      $ngModel.$isEmpty = function isEmpty(val) {
        return !val || !val.length;
      };

      let scrollToContainer = null;
      if ('scrollToOnOpen' in $attrs) {
        if ($attrs.scrollToOnOpen === 'window') {
          scrollToContainer = $document.documentElement;
        } else {
          scrollToContainer = $element.closest($attrs.scrollToOnOpen)[0];
        }
      }

      if ($scope.createEnabled) {
        $scope.createCallback = function createCallback() {
          const promises = ($scope.selected || [])
            .filter((persona) => !persona.id)
            .map((persona) => {
              switch (persona.type) {
                case 'position':
                case 'location':
                case 'group':
                  return Groups.withHttpConfig({ ignoreGlobalErrorHandler: true })
                    .post(persona)
                    .then((data) => {
                      delete persona.$$new;
                      angular.extend(persona, data);
                      return data;
                    });
                case 'tag':
                  return TagsService.saveTag(persona).then((data) => {
                    delete persona.$$new;
                    angular.extend(persona, data);
                    return data;
                  });
                case 'user':
                  return Users.withHttpConfig({ ignoreGlobalErrorHandler: true })
                    .post(persona)
                    .then((data) => {
                      delete persona.$$new;
                      angular.extend(persona, data);
                      return data;
                    });
                default:
                  throw new Error(
                    "[persona-picker] new persona's type must be user or location or position or group"
                  );
              }
            });

          return $q.all(promises);
        };
      }

      $scope.$watch(
        'ctrl.$searchObject.items[ctrl.$searchObject.activeIndex]',
        (highlightedPersona) => {
          $scope.highlightedPersona = highlightedPersona;
        }
      );

      $scope.$on('search-lookup-open', () => {
        $scope.focused = true;

        if ($scope.onFocus) {
          $scope.onFocus();
        }

        if (scrollToContainer) {
          $timeout(() => {
            const rect = $element[0].querySelector('.search-lookup').getBoundingClientRect();
            const containerRect = scrollToContainer.getBoundingClientRect();

            if (rect.bottom > containerRect.bottom) {
              scrollToContainer.scrollTop += rect.bottom - containerRect.bottom;
            }
          });
        }
      });

      $scope.$on('search-lookup-close', () => {
        $scope.focused = false;
        $scope.ctrl.stagedToDelete = false;
      });
    },
  };
}
